import React, { useState, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faFileUpload } from '@fortawesome/free-solid-svg-icons';
import { useApiClient } from '../../service/API';
import { getApiErrorMessage } from "../../service/errorHandler";
import '../../styling/FileUpload.css';
import NotificationModal from '../TicketComponents/NotificationModal';
import { formToJSON } from 'axios';

const FileUpload = ({ticketId = "", setFileUploading, files, setFiles, ticketFolderName, setTicketFolderName, checkFile, type }) => {
  const [error, setError] = useState('');
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const apiClient = useApiClient();

  // Define allowed file types and max size
  const allowedTypes = [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/pdf', // .pdf
    'image/jpeg', // .jpg, .jpeg
    'image/png', // .png
    'application/vnd.ms-excel', // .xls
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'message/rfc822', // .eml
    'application/vnd.ms-outlook' // .msg
  ];
  
  const allowedExtensions = ['.doc', '.docx', '.pdf', '.jpg', '.jpeg', '.png', '.xls', '.xlsx', '.eml', '.msg'];
  const maxSize = 10 * 1024 * 1024; // 10 MB

  const isFileAlreadyUploaded = (file) => {
    if (checkFile?.length > 0) {
      return (
        checkFile.some(existingFile => existingFile.name === file.name) ||
        files.some(existingFile => existingFile.name === file.name)
      );
    }
  
    return files.some(existingFile => existingFile.name === file.name);
  };  

  const isFileValid = (file) => {
    // Check file extension
    const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    const isValidExtension = allowedExtensions.includes(extension);
    
    // For drag and drop, sometimes the file.type might not be accurate for all file types
    // especially for .msg files, so we rely on file extension
    if (!isValidExtension) {
      setError(`Invalid file type: ${file.name}. Please upload only supported file types.`);
      return false;
    }

    // Check file size
    if (file.size > maxSize) {
      setError(`File size exceeds the maximum allowed size of 10 MB: ${file.name}`);
      return false;
    }

    return true;
  };

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);
  
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);
  
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
  }, [isDragging]);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setError('');
    
    try {
      // Log all available formats
      console.log('Available formats:', Array.from(e.dataTransfer.types));
      
      // Try to get standard files first
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const droppedFiles = Array.from(e.dataTransfer.files);
        handleValidFiles(droppedFiles);
        return;
      }
      
      // Check for maillistrow JSON data
      const hasMailListRow = Array.from(e.dataTransfer.types).includes('maillistrow');
      if (hasMailListRow) {
        try {
          const mailListData = e.dataTransfer.getData('maillistrow');
          const mailData = JSON.parse(mailListData);
          console.log('Parsed maillistrow data:', mailData);
          
          // Extract available metadata
          if (mailData.subjects && mailData.subjects.length > 0) {
            const subject = mailData.subjects[0];
            let senderInfo = '';
            
            // Try to get sender information if available
            if (mailData.mailboxInfos && mailData.mailboxInfos.length > 0) {
              senderInfo = mailData.mailboxInfos[0].mailboxSmtpAddress || '';
            }
            
            // Show notification that we can't get full content
            setNotificationMessage(
              `Direct email content is not available through drag and drop.\n` +
              `Please save the email as .msg or .eml first, then upload the file.`
            );
            setShowNotification(true);
          }
        } catch (err) {
          console.error('Error parsing maillistrow data:', err);
        }
      }
      
    } catch (error) {
      console.error('Error processing drop:', error);
      setError('Error processing drop. Please try saving the email as a file first.');
    }
  }, [files, ticketFolderName]);
  
  // Helper function to validate and upload files
  const handleValidFiles = (filesToCheck) => {
    const validFiles = [];
    
    for (const file of filesToCheck) {
      if (isFileAlreadyUploaded(file)) {
        setError('One or more files have already been uploaded.');
        continue;
      }
      
      if (!isFileValid(file)) {
        continue;
      }
      
      validFiles.push(file);
    }
    
    if (validFiles.length > 0) {
      uploadFilesToServer(validFiles);
    } else {
      setError('No valid files were found.');
    }
  };

  const uploadFilesToServer = async (filesToUpload) => {
    try {
      setFileUploading(true);
      const formData = new FormData();
      formData.append('folderName', ticketFolderName);
      formData.append('type',type);
      formData.append('ticketId',ticketId);
      
      filesToUpload.forEach(file => formData.append('filename', file));
      const response = await apiClient.post('/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.status === 200) {
        setNotificationMessage(`${filesToUpload.length} file(s) uploaded successfully!`);
        setTicketFolderName(response.data.folderName);
        setFiles(prevFiles => [...prevFiles, ...response.data.fileResponse]);
        setShowNotification(true);
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      setNotificationMessage(
        getApiErrorMessage(error, "Error uploading files. Please try again.")
      );
      setShowNotification(true);
    } finally {
      setFileUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
    }
  };

  const uploadHandler = (event) => {
    event.preventDefault();
    const selectedFiles = Array.from(event.target.files);
    if (selectedFiles.length === 0) return;

    setError(''); // Clear previous error
    
    const validFiles = [];
    let hasError = false;
    
    // Validate all files
    for (const file of selectedFiles) {
      console.log(isFileAlreadyUploaded(file));
      if (isFileAlreadyUploaded(file)) {
        setError('One or more files have already been uploaded.');
        hasError = true;
        continue;
      }
      
      if (!isFileValid(file)) {
        hasError = true;
        continue;
      }
      
      validFiles.push(file);
    }
    
    if (validFiles.length === 0) {
      if (!hasError) {
        setError('No valid files were selected.');
      }
      // Reset the file input value
      fileInputRef.current.value = null;
      return;
    }
    
    // Handle the valid files
    uploadFilesToServer(validFiles);
  };

  const handleCloseNotification = () => {
    setShowNotification(false);
  };

  const handleButtonClick = (event) => {
    event.preventDefault();
    fileInputRef.current.click();
  };

  return (
    <div 
      className={`file-card-upload ${isDragging ? 'dragging' : ''}`}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="file-inputs">
        <input
          type="file"
          ref={fileInputRef}
          onChange={uploadHandler}
          accept=".docx, .pdf, .jpg, .jpeg, .png, .xls, .xlsx, .eml, .msg"
          multiple={type!=='QUOTATION'}
        />
        <button onClick={handleButtonClick} className="upload-button">
          <FontAwesomeIcon icon={faFileUpload} className="icon" /> Upload Files
        </button>
      </div>
      
      {error && <p className="error">{error}</p>}
      
      <div className="drag-area-message">
        {isDragging ? (
          <p className="drag-active">Drop emails or files here to upload</p>
        ) : (
          <p className="drag-inactive">or files from your computer</p>
        )}
      </div>
      
      <p className="main">Supported files</p>
      <p className="info">
        .doc, .docx, .pdf, .jpg, .jpeg, .png, .xls, .xlsx, .eml, or .msg
        <br />
        (Each file should not exceed 10 MB)
      </p>

      {/* Notification Modal */}
      <NotificationModal
        show={showNotification}
        message={notificationMessage}
        onClose={handleCloseNotification}
      />
    </div>
  );
};

export default FileUpload;